import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosResult, AxiosResultDefaultError } from '../../../api/request';
import { createFetchContext } from '../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../components/info/DisplayError';
import InputLoading from '../../../components/info/InputLoading';
import { getClassroomAvailability } from '../api';
import { useSchedulesLoader } from '../contexts/SchedulesLoader';
import { LessonsPeriodicity } from '../../../types/sectionMaintainerTypes';

const prefix = 'sectionsMaintainer.update';

export interface MaintainerScheduleClassroomLoaderFilters {
  campusId?: number;
  day?: string;
  startModuleId?: number;
  endModuleId?: number;
  startDate?: string;
  endDate?: string;
  sectionScheduleId?: number;
  lessonsPeriodicity?: LessonsPeriodicity;
}

interface BaseLoaderProps {
  filters: MaintainerScheduleClassroomLoaderFilters;
  children: ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  MaintainerScheduleClassroomLoaderFilters,
  any[] | undefined,
  AxiosResultDefaultError
>();

export const useMaintainerScheduleClassroomLoader = useFetch;

export const formDataLoaderConsumer = FetchConsumer;

export default function MaintainerScheduleClassroomLoader({
  children,
  filters,
}: BaseLoaderProps) {
  const { data } = useSchedulesLoader();
  const { t } = useTranslation();

  const request = useCallback(async () => {
    const { sectionScheduleId, ...requiredFilters } = filters;
    if (Object.values(requiredFilters).some((value) => value === undefined)) {
      return Promise.resolve<AxiosResult<any[], AxiosResultDefaultError>>(
        [] as any,
      );
    }
    return getClassroomAvailability(data!.section.id, filters);
  }, [data, filters]);

  return (
    <FetchProvider
      request={request}
      defaultQuery={filters}
      fetchImmediately={true}
    >
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) {
            return <InputLoading inputLabel={t(`${prefix}.classroom`)} />;
          } else if (error) {
            return <DisplayError textBody={error.code} retryAction={refresh} />;
          } else if (data) {
            return children;
          } else {
            return null;
          }
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
